import { LogisticsData, LogisticsOption, DayItinerary, TimeSlot } from '../types';

// ─── URL VALIDATION CACHE ───────────────────────────────────────────
// Cache validated URLs so repeat venues use known-good links instantly.
// Key: venue name + type + destination → Value: validated URL
const urlCache = new Map<string, { url: string; timestamp: number }>();
const URL_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export const getCachedUrl = (name: string, type: string, destination?: string): string | null => {
  const key = `${name}|${type}|${destination || ''}`.toLowerCase();
  const cached = urlCache.get(key);
  if (cached && Date.now() - cached.timestamp < URL_CACHE_TTL) return cached.url;
  if (cached) urlCache.delete(key);
  return null;
};

export const setCachedUrl = (name: string, type: string, destination: string | undefined, url: string): void => {
  const key = `${name}|${type}|${destination || ''}`.toLowerCase();
  urlCache.set(key, { url, timestamp: Date.now() });
};

// ─── TYPES ──────────────────────────────────────────────────────────

export interface ValidatedLink {
  isValid: boolean;
  url: string;
  confidence: number;
  fallbackUrl: string;
}

// ─── AFFILIATE CONFIGURATION ────────────────────────────────────────

/**
 * AURA TRAVELER AFFILIATE CONFIGURATION
 *
 * To receive commission on bookings, enter your affiliate IDs/Tags below.
 * If a field is left blank, the app will use direct links without affiliate tracking.
 *
 * LINK ACCURACY POLICY: direct booking/reservation links ALWAYS win over
 * affiliate links. Affiliate tracking is only ever applied as URL parameters
 * appended to the page the user was already going to (e.g. a `tag` on Amazon,
 * an `aid` on a booking.com deep link). Affiliate wrapping NEVER redirects a
 * venue link to a different site — replacing the Ritz Carlton's reservation
 * page with a generic Expedia or Viator search page is forbidden. The only
 * exception is provider CTAs (eSIM, transfers) where the affiliate landing
 * page IS the product page.
 */
export const AFFILIATE_CONFIG = {
  // Amazon Associates (for Packing List)
  amazon: "auratravele0b-20",

  // Stay22 (Hotels & Airbnbs)
  stay22: "auratraveler",

  // GetYourGuide (Tours & Activities)
  getYourGuide: "",

  // TravelPayouts (Flights & Hotels)
  travelPayouts: "508736",

  // Tinggly (Experience Gifts)
  tinggly: "",

  // Saily (eSIMs)
  saily: "",

  // Amigo eSIM
  amigoEsim: "",

  // Yesim (eSIMs)
  yesim: "https://yesim.tpx.lv/aUGPBhen",

  // GetTransfer (Transfers & Car Rentals)
  getTransfer: "https://gettransfer.tpx.lv/2d7tZO3C",

  // Viator (Tours & Activities)
  viator: "P00293047",

  // Booking.com Affiliate Partner (Hotels)
  // Sign up at https://www.booking.com/affiliate-program/v2/index.html
  bookingCom: "",

  // Expedia Creator / TAAP attribution shortlink.
  // From https://creator.expediagroup.com/app/dash → your Creator profile.
  // Format: https://www.expedia.com/affiliate/<7-10 char ID> — visiting it
  // sets a cookie that attributes any Expedia booking within the cookie
  // window (typically 7 days) to your account. The shortlink does NOT
  // accept deep-link query params, so all Expedia hotel clicks route to
  // this single URL; users then search for the specific hotel on Expedia.
  // Trade-off: less precise UX, but reliable cookie attribution.
  // When non-empty, hotels default to Expedia (higher commission than Stay22).
  expedia: "https://www.expedia.com/affiliate/qVHMEW9",
};

// ─── eSIM PROVIDERS ─────────────────────────────────────────────────

export interface ESimProvider {
  name: string;
  url: string;
}

/** Normalize a country name into a URL-safe slug (e.g. "Indonesia" → "indonesia") */
const countrySlug = (country: string): string =>
  country.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

/** Get a direct eSIM purchase link for a given provider and destination country. */
export const getESimLink = (provider: string | undefined, country: string): string => {
  const slug = countrySlug(country || '');
  const name = (provider || '').toLowerCase().trim();

  if (name.includes('saily') || name.includes('saily.com')) {
    const base = slug ? `https://saily.com/esim-${slug}/` : 'https://saily.com/';
    return AFFILIATE_CONFIG.saily ? `${base}?tag=${AFFILIATE_CONFIG.saily}` : base;
  }

  if (name.includes('airalo')) {
    return slug ? `https://www.airalo.com/${slug}` : 'https://www.airalo.com/';
  }

  if (name.includes('yesim')) {
    return AFFILIATE_CONFIG.yesim || 'https://yesim.app/';
  }

  if (name.includes('amigo')) {
    const base = 'https://esimamigo.com/';
    return AFFILIATE_CONFIG.amigoEsim ? `${base}?ref=${AFFILIATE_CONFIG.amigoEsim}` : base;
  }

  if (name.includes('holafly')) {
    return slug ? `https://esim.holafly.com/esim-${slug}/` : 'https://esim.holafly.com/';
  }

  // Default to Saily with country slug
  return slug ? `https://saily.com/esim-${slug}/` : 'https://saily.com/';
};

/** Get all known eSIM providers with links for a given country. */
export const getAllESimProviders = (country: string): ESimProvider[] => {
  const slug = countrySlug(country || '');
  const providers: ESimProvider[] = [
    { name: 'Saily', url: slug ? `https://saily.com/esim-${slug}/` : 'https://saily.com/' },
    { name: 'Airalo', url: slug ? `https://www.airalo.com/${slug}` : 'https://www.airalo.com/' },
    { name: 'Holafly', url: slug ? `https://esim.holafly.com/esim-${slug}/` : 'https://esim.holafly.com/' },
  ];

  // Add Yesim if affiliate link configured
  if (AFFILIATE_CONFIG.yesim) {
    providers.push({ name: 'Yesim', url: AFFILIATE_CONFIG.yesim });
  }

  return providers;
};

// ─── CORE VALIDATION ────────────────────────────────────────────────

/**
 * Validates that a URL looks like a real, functional booking URL.
 * Permissive by design — accepts any valid URL with a real domain.
 * Returns false only for clearly broken/fake URLs.
 */
/** Well-known booking/travel platforms where deep paths are trustworthy */
const TRUSTED_DEEP_LINK_DOMAINS = [
  'booking.com', 'expedia.com', 'hotels.com', 'airbnb.com', 'vrbo.com',
  'tripadvisor.com', 'agoda.com', 'trivago.com', 'kayak.com', 'priceline.com',
  'hilton.com', 'marriott.com', 'ihg.com', 'hyatt.com', 'accor.com',
  'viator.com', 'getyourguide.com', 'klook.com', 'tiqets.com',
  'google.com', 'maps.google.com', 'amazon.com', 'opentable.com', 'resy.com',
  'skyscanner.com', 'momondo.com', 'wego.com',
  'stay22.com', 'travelpayouts.com',
];

const isTrustedDomain = (hostname: string): boolean =>
  TRUSTED_DEEP_LINK_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d));

export const isValidBookingUrl = (url: string | undefined): boolean => {
  if (!url || typeof url !== 'string' || url.trim().length < 10) return false;
  try {
    const u = new URL(url);
    // Must be http or https
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    // Must have a real domain (not localhost, not IP, not example.com)
    if (/^(localhost|127\.|192\.168\.|10\.|example\.com)/i.test(u.hostname)) return false;
    // Must have a TLD
    if (!/\.\w{2,}$/.test(u.hostname)) return false;
    return true;
  } catch {
    return false;
  }
};

/**
 * Detects URLs that are likely hallucinated by AI models.
 * These pass isValidBookingUrl() (valid format) but point to non-existent pages.
 * Examples: "https://booking.com/hotel/fake-hotel-12345", random deep paths on trusted domains.
 */
/** Domains of known legitimate booking/travel/review platforms.
 *  URLs on these domains are NOT flagged as hallucinated — even if the deep path
 *  might 404, the platform itself is real and the search experience is useful. */
const KNOWN_GOOD_DOMAINS = [
  'booking.com', 'hotels.com', 'expedia.com', 'tripadvisor.com',
  'opentable.com', 'yelp.com', 'viator.com', 'getyourguide.com',
  'airbnb.com', 'marriott.com', 'hilton.com', 'hyatt.com', 'ihg.com',
  'agoda.com', 'trivago.com', 'kayak.com', 'priceline.com',
  'vrbo.com', 'klook.com', 'tiqets.com', 'skyscanner.com',
  'momondo.com', 'accor.com', 'resy.com', 'google.com',
  'wego.com', 'stay22.com', 'travelpayouts.com', 'amazon.com',
];

export const isKnownGoodDomain = (hostname: string): boolean => {
  const bare = hostname.replace(/^www\./, '');
  return KNOWN_GOOD_DOMAINS.some(d => bare === d || bare.endsWith('.' + d));
};

/**
 * Detects affiliate-redirect URLs persisted into saved trips by the OLD
 * (pre-accuracy-policy) wrapping logic: the Aviasales homepage marker link,
 * the static Expedia Creator shortlink, Viator search redirects, and Stay22
 * search pages. These point at a generic affiliate page instead of the venue,
 * so for venue links they must be treated as invalid and replaced with an
 * accurate fallback (Google Maps venue search / airline site).
 */
export const isLegacyAffiliateRedirect = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'aviasales.com' && u.searchParams.has('marker') && (u.pathname === '/' || u.pathname === '')) return true;
    if (host === 'expedia.com' && u.pathname.startsWith('/affiliate/')) return true;
    if (host === 'viator.com' && u.pathname.startsWith('/searchResults')) return true;
    if (host === 'stay22.com' || host.endsWith('.stay22.com')) return true;
    return false;
  } catch {
    return false;
  }
};

export const isLikelyHallucinatedUrl = (url: string): boolean => {
  try {
    const u = new URL(url);
    const hostname = u.hostname.replace(/^www\./, '');
    const path = u.pathname;

    // ── Whitelist: known booking/travel platforms are NEVER hallucinated ──
    if (isKnownGoodDomain(hostname)) {
      return false;
    }

    // ── Generic hallucination patterns (for unknown domains) ──

    // Placeholder-like segments: /fake-hotel, /test-restaurant, /example-tour
    if (/\/(fake|test|example|placeholder|sample|demo|dummy)-/i.test(path)) {
      return true;
    }

    // Sequential numeric IDs that AIs love to invent: /hotel-12345, /venue-99999
    if (/\/[\w]+-\d{4,}$/i.test(path)) {
      return true;
    }

    // Random hex strings in paths: /a3f8b2c1d4e5 (8+ hex chars in a row)
    if (/\/[0-9a-f]{8,}\b/i.test(path) && !/\.\w{2,4}$/.test(path)) {
      return true;
    }

    // Placeholder domains (not real TLDs)
    if (/\.(invalid|example|localhost|test|internal)$/i.test(hostname)) {
      return true;
    }

    // Suspicious domain patterns: random-word-word-word.com (4+ hyphenated segments)
    const domainParts = hostname.split('.');
    const mainDomain = domainParts.length >= 2 ? domainParts[domainParts.length - 2] : '';
    if (mainDomain.split('-').length >= 4) {
      return true;
    }

    // Generic travel/info domains that Gemini uses as "sources" instead of venue websites
    // e.g., machupicchu.org for a restaurant, cuscoperu.com for a specific hotel
    const genericTravelDomains = [
      'tripadvisor.', 'lonelyplanet.', 'timeout.', 'eater.', 'yelp.',
      'machupicchu.org', 'cuscoperu.', 'visitperu.', 'peru.travel',
      'wikipedia.org', 'wikivoyage.org', 'wikitravel.org',
      'theculturetrip.', 'atlasobscura.', 'fodors.', 'frommers.',
      'roughguides.', 'worldtravelguide.',
    ];
    if (genericTravelDomains.some(d => hostname.includes(d) || hostname === d.replace(/\.$/, ''))) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Sanitizes AI-generated URLs: for unknown hotel/restaurant domains,
 * strips deep paths that are likely hallucinated (e.g. /taste/bacan).
 * Trusted booking platforms keep their full paths.
 */
export const sanitizeAiUrl = (url: string): string => {
  try {
    const u = new URL(url);
    if (isTrustedDomain(u.hostname)) return url;
    // For unknown domains (individual hotel/restaurant sites),
    // only keep paths 1 level deep — deeper paths are usually hallucinated
    const segments = u.pathname.split('/').filter(Boolean);
    if (segments.length > 1) {
      // Strip to root — the homepage is far more likely to work
      return `${u.protocol}//${u.host}/`;
    }
    return url;
  } catch {
    return url;
  }
};

// ─── GUARANTEED FALLBACK URLS ───────────────────────────────────────

/**
 * Builds the most useful fallback URL for a venue.
 * Priority: Google Maps (shows real venue info + website link) > search pages.
 * Google Maps is the best fallback because it shows the venue's real info,
 * hours, reviews, photos, AND links to their actual website.
 */
export const buildGuaranteedUrl = (
  name: string,
  type: 'flight' | 'hotel' | 'restaurant' | 'activity' | 'attraction',
  destination?: string
): string => {
  const query = encodeURIComponent(`${name}${destination ? ' ' + destination : ''}`);
  const nameOnly = encodeURIComponent(name);
  const destOnly = destination ? encodeURIComponent(destination) : '';

  switch (type) {
    case 'flight':
      // Airlines: use their common domain patterns or Google Flights
      return buildAirlineUrl(name) || `https://www.google.com/travel/flights?q=${query}`;
    case 'hotel':
      // Hotels: Google Maps shows the hotel with direct website link
      return `https://www.google.com/maps/search/${nameOnly}+hotel${destOnly ? '+' + destOnly : ''}`;
    case 'restaurant':
      // Restaurants: Google Maps shows the venue with website, menu, reservations
      return `https://www.google.com/maps/search/${nameOnly}${destOnly ? '+' + destOnly : ''}`;
    case 'activity':
    case 'attraction':
      // Attractions: Google Maps shows hours, tickets, and official website
      return `https://www.google.com/maps/search/${nameOnly}${destOnly ? '+' + destOnly : ''}`;
    default:
      return `https://www.google.com/search?q=${query}`;
  }
};

/**
 * Maps common airline names to their actual website domains.
 * Returns undefined if airline not recognized (falls back to Google Flights).
 */
const AIRLINE_DOMAINS: Record<string, string> = {
  'delta': 'https://www.delta.com', 'united': 'https://www.united.com',
  'american': 'https://www.aa.com', 'southwest': 'https://www.southwest.com',
  'jetblue': 'https://www.jetblue.com', 'alaska': 'https://www.alaskaair.com',
  'spirit': 'https://www.spirit.com', 'frontier': 'https://www.flyfrontier.com',
  'british airways': 'https://www.britishairways.com', 'lufthansa': 'https://www.lufthansa.com',
  'air france': 'https://www.airfrance.com', 'klm': 'https://www.klm.com',
  'emirates': 'https://www.emirates.com', 'qatar': 'https://www.qatarairways.com',
  'singapore airlines': 'https://www.singaporeair.com', 'cathay pacific': 'https://www.cathaypacific.com',
  'ana': 'https://www.ana.co.jp/en', 'jal': 'https://www.jal.co.jp/en',
  'turkish airlines': 'https://www.turkishairlines.com', 'etihad': 'https://www.etihad.com',
  'qantas': 'https://www.qantas.com', 'iberia': 'https://www.iberia.com',
  'swiss': 'https://www.swiss.com', 'austrian': 'https://www.austrian.com',
  'tap portugal': 'https://www.flytap.com', 'ryanair': 'https://www.ryanair.com',
  'easyjet': 'https://www.easyjet.com', 'norwegian': 'https://www.norwegian.com',
  'vueling': 'https://www.vueling.com', 'wizz air': 'https://wizzair.com',
  'air canada': 'https://www.aircanada.com', 'westjet': 'https://www.westjet.com',
  'avianca': 'https://www.avianca.com', 'latam': 'https://www.latamairlines.com',
  'copa': 'https://www.copaair.com', 'aeromexico': 'https://www.aeromexico.com',
  'korean air': 'https://www.koreanair.com', 'thai airways': 'https://www.thaiairways.com',
  // Additional carriers
  'air asia': 'https://www.airasia.com', 'airasia': 'https://www.airasia.com',
  'lion air': 'https://www.lionair.co.id', 'cebu pacific': 'https://www.cebupacificair.com',
  'scoot': 'https://www.flyscoot.com', 'indigo': 'https://www.goindigo.in',
  'air india': 'https://www.airindia.com', 'vistara': 'https://www.airvistara.com',
  'china southern': 'https://www.csair.com', 'china eastern': 'https://www.ceair.com',
  'air china': 'https://www.airchina.com', 'hainan airlines': 'https://www.hainanairlines.com',
  'eva air': 'https://www.evaair.com', 'china airlines': 'https://www.china-airlines.com',
  'philippine airlines': 'https://www.philippineairlines.com',
  'air europa': 'https://www.aireuropa.com', 'aegean': 'https://www.aegeanair.com',
  'lot polish': 'https://www.lot.com', 'finnair': 'https://www.finnair.com',
  'icelandair': 'https://www.icelandair.com', 'sas': 'https://www.flysas.com',
  'aer lingus': 'https://www.aerlingus.com', 'volotea': 'https://www.volotea.com',
  'pegasus': 'https://www.flypgs.com', 'sun country': 'https://www.suncountry.com',
  'breeze': 'https://www.flybreeze.com', 'play': 'https://www.flyplay.com',
  'royal jordanian': 'https://www.rj.com', 'oman air': 'https://www.omanair.com',
  'saudia': 'https://www.saudia.com', 'gulf air': 'https://www.gulfair.com',
  'ethiopian airlines': 'https://www.ethiopianairlines.com',
  'south african airways': 'https://www.flysaa.com',
  'virgin atlantic': 'https://www.virginatlantic.com', 'virgin australia': 'https://www.virginaustralia.com',
  'hawaiian airlines': 'https://www.hawaiianairlines.com',
  'vietnam airlines': 'https://www.vietnamairlines.com', 'garuda': 'https://www.garuda-indonesia.com',
  'air new zealand': 'https://www.airnewzealand.com', 'fiji airways': 'https://www.fijiairways.com',
  'south african': 'https://www.flysaa.com', 'kenya airways': 'https://www.kenya-airways.com',
  'royal air maroc': 'https://www.royalairmaroc.com', 'egyptair': 'https://www.egyptair.com',
};
const buildAirlineUrl = (name: string): string | undefined => {
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(AIRLINE_DOMAINS)) {
    if (lower.includes(key)) return url;
  }
  return undefined;
};

// ─── AFFILIATE WRAPPING ─────────────────────────────────────────────

/**
 * Wraps a URL with affiliate tracking parameters.
 * NEVER returns empty string — if wrapping fails, returns the original URL.
 * NEVER redirects: tracking is only added as query params on matching hosts,
 * so the user always lands on the exact page the link promised.
 * (locationHint is retained for API compatibility; redirect-style affiliates
 * that needed it have been removed under the link-accuracy policy.)
 */
export const wrapAffiliateLink = (url: string, type?: string, _locationHint?: string): string => {
  // Safety: never return empty
  if (!url || url.trim() === '') return url || '';
  if (!type) return url;

  try {
    // Enforce HTTPS
    let safeUrl = url;
    if (safeUrl.startsWith('http://')) {
      safeUrl = safeUrl.replace('http://', 'https://');
    }

    // Validate URL structure before processing
    const urlObj = new URL(safeUrl);
    // Track whether tracking params were actually added — if not, return the
    // original URL byte-for-byte (URL.toString() would normalize it).
    let modified = false;

    switch (type.toLowerCase()) {
      case 'amazon':
        if (AFFILIATE_CONFIG.amazon && urlObj.hostname.includes('amazon.')) {
          urlObj.searchParams.set('tag', AFFILIATE_CONFIG.amazon);
          modified = true;
        }
        break;

      case 'stay22':
        // LINK ACCURACY: never replace the hotel's real URL (or an accurate
        // Google Maps result) with a Stay22 search page. Only tag URLs that
        // are already on stay22.com.
        if (AFFILIATE_CONFIG.stay22 && urlObj.hostname.includes('stay22.com')) {
          urlObj.searchParams.set('aid', AFFILIATE_CONFIG.stay22);
          urlObj.searchParams.set('campaign', 'auratraveler');
          modified = true;
        }
        break;

      case 'getyourguide':
        if (AFFILIATE_CONFIG.getYourGuide && urlObj.hostname.includes('getyourguide.com')) {
          urlObj.searchParams.set('partner_id', AFFILIATE_CONFIG.getYourGuide);
          modified = true;
        }
        break;

      case 'travelpayouts':
        // LINK ACCURACY: only attach the marker to URLs already on a
        // TravelPayouts-supported host. Never redirect an airline's own
        // booking page (or Google Flights) to the Aviasales homepage.
        if (AFFILIATE_CONFIG.travelPayouts) {
          const tpHosts = ['aviasales.com', 'hotellook.com', 'tickets.aviasales.com', 'jetradar.com'];
          if (tpHosts.some(h => urlObj.hostname.includes(h))) {
            urlObj.searchParams.set('marker', AFFILIATE_CONFIG.travelPayouts);
            modified = true;
          }
        }
        break;

      case 'saily':
        if (AFFILIATE_CONFIG.saily) {
          urlObj.searchParams.set('tag', AFFILIATE_CONFIG.saily);
          modified = true;
        }
        break;

      case 'amigoesim':
        if (AFFILIATE_CONFIG.amigoEsim) {
          urlObj.searchParams.set('ref', AFFILIATE_CONFIG.amigoEsim);
          modified = true;
        }
        break;

      case 'yesim':
        if (AFFILIATE_CONFIG.yesim) {
          return AFFILIATE_CONFIG.yesim;
        }
        break;

      case 'gettransfer':
        if (AFFILIATE_CONFIG.getTransfer) {
          return AFFILIATE_CONFIG.getTransfer;
        }
        break;

      case 'viator':
        // LINK ACCURACY: only tag URLs already on viator.com. Never redirect
        // a venue's own ticket/reservation page to a Viator search page.
        if (AFFILIATE_CONFIG.viator && urlObj.hostname.includes('viator.com')) {
          urlObj.searchParams.set('pid', AFFILIATE_CONFIG.viator);
          urlObj.searchParams.set('mcid', '42383');
          urlObj.searchParams.set('medium', 'link');
          modified = true;
        }
        break;

      case 'tinggly':
        if (AFFILIATE_CONFIG.tinggly) {
          urlObj.searchParams.set('ref', AFFILIATE_CONFIG.tinggly);
          modified = true;
        }
        break;

      case 'bookingcom':
        if (AFFILIATE_CONFIG.bookingCom && urlObj.hostname.includes('booking.com')) {
          urlObj.searchParams.set('aid', AFFILIATE_CONFIG.bookingCom);
          modified = true;
        }
        break;

      case 'expedia':
        // LINK ACCURACY: the Expedia Creator shortlink is a static redirect
        // that loses the specific hotel entirely — sending a user who clicked
        // "Hotel Lutetia" to the Expedia homepage. We no longer redirect venue
        // links through it. The shortlink remains available in AFFILIATE_CONFIG
        // for explicit "Compare on Expedia" placements, and expedia.com deep
        // links pass through untouched (already the accurate page).
        break;

      default:
        return safeUrl;
    }

    return modified ? urlObj.toString() : safeUrl;
  } catch {
    // If anything fails, return the original URL — NEVER empty
    return url;
  }
};

// ─── SINGLE ENTRY POINT ─────────────────────────────────────────────

/**
 * THE SINGLE SOURCE OF TRUTH for booking links.
 * Guarantees a working URL is ALWAYS returned.
 * Never returns empty string.
 *
 * Usage:
 *   const link = getBookingLink(slot.bookingUrl, slot.activity, 'activity', destination, slot.affiliateType);
 *   // link is GUARANTEED to be a working URL — no need for {link && ...} guards
 */
export const getBookingLink = (
  aiUrl: string | undefined,
  name: string,
  type: 'flight' | 'hotel' | 'restaurant' | 'activity' | 'attraction',
  destination?: string,
  affiliateType?: string
): string => {
  // Check cache first — repeat venues get known-good URLs instantly
  const cached = getCachedUrl(name, type, destination);
  if (cached && !isLegacyAffiliateRedirect(cached)) return cached;

  // Build a location hint for affiliates like Stay22 that need a physical address
  const locationHint = [name, destination].filter(Boolean).join(', ');

  // Venue links may only carry type-appropriate, accuracy-preserving affiliate
  // tags. Gemini sometimes labels a restaurant 'travelpayouts' or 'gettransfer';
  // letting those through would route a dinner reservation to a flight or
  // transfer affiliate page. Anything not in this allowlist is dropped.
  const ALLOWED_VENUE_AFFILIATES: Record<string, string[]> = {
    flight: ['travelpayouts'],
    hotel: ['bookingcom', 'stay22'],
    restaurant: [],
    activity: ['viator', 'getyourguide'],
    attraction: ['viator', 'getyourguide'],
  };
  if (affiliateType && !ALLOWED_VENUE_AFFILIATES[type]?.includes(affiliateType.toLowerCase())) {
    affiliateType = undefined;
  }

  // Default affiliate type by entity if Gemini didn't specify one.
  // All wraps are non-destructive (param-append on matching hosts only), so
  // these defaults simply tag affiliate-platform deep links when they occur
  // naturally — they never redirect away from an accurate venue URL.
  // Restaurants intentionally have no default (no good affiliate fit).
  if (!affiliateType) {
    if (type === 'hotel') affiliateType = 'bookingcom';
    else if (type === 'flight') affiliateType = 'travelpayouts';
    else if (type === 'activity' || type === 'attraction') affiliateType = 'viator';
  }

  // Step 1: Try the AI-generated URL — sanitize deep paths, then wrap with affiliate tracking
  if (isValidBookingUrl(aiUrl) && !isLegacyAffiliateRedirect(aiUrl)) {
    // Step 1a: Reject likely-hallucinated URLs (e.g., fake deep links on unknown domains)
    if (isLikelyHallucinatedUrl(aiUrl!)) {
      console.warn(
        `[LinkService] AI-generated URL likely hallucinated for "${name}":`,
        `"${aiUrl!.substring(0, 80)}" — replacing with guaranteed fallback`
      );
    } else {
      // Step 1b: Keep the AI-generated URL — it's likely a real venue website.
      // Known booking platforms keep full URLs. Unknown domains (venue websites) keep
      // short paths but strip suspiciously deep ones (4+ segments).
      let cleanUrl: string;
      try {
        const parsed = new URL(aiUrl!);
        const hostname = parsed.hostname.replace(/^www\./, '');
        if (isKnownGoodDomain(hostname)) {
          // Known booking platform — keep full URL
          cleanUrl = aiUrl!;
        } else {
          // Likely a real venue website — keep if path is reasonable (≤3 segments)
          const segments = parsed.pathname.split('/').filter(Boolean);
          if (segments.length <= 3) {
            // Short path like /menu, /reservations, /en/visit — trust it
            cleanUrl = aiUrl!;
          } else {
            // Suspiciously deep path — strip to root
            cleanUrl = `${parsed.protocol}//${parsed.host}/`;
          }
        }
      } catch {
        cleanUrl = sanitizeAiUrl(aiUrl!);
      }

      if (affiliateType) {
        const wrapped = wrapAffiliateLink(cleanUrl, affiliateType, locationHint);
        if (wrapped !== cleanUrl) return wrapped;
      }
      return cleanUrl;
    }
  }

  // Step 2: GUARANTEED fallback — buildGuaranteedUrl ALWAYS returns a working search URL
  const fallback = buildGuaranteedUrl(name || 'travel', type, destination);
  if (affiliateType) {
    const wrapped = wrapAffiliateLink(fallback, affiliateType, locationHint);
    if (wrapped !== fallback) return wrapped;
  }

  // SAFETY NET: fallback should never be empty, but guard against it
  if (!fallback || fallback.trim() === '') {
    console.warn(`[LinkService] CRITICAL: buildGuaranteedUrl returned empty for "${name}" — using Google search`);
    return `https://www.google.com/search?q=${encodeURIComponent((name || 'travel') + ' ' + (destination || ''))}`;
  }

  return fallback;
};

// ─── BACKWARD COMPATIBILITY ALIASES ─────────────────────────────────

/**
 * @deprecated Use getBookingLink() instead. Kept for backward compatibility.
 */
export const getGuaranteedBookingUrl = (
  providedUrl: string | undefined,
  name: string,
  location: string,
  type: 'airline' | 'hotel' | 'restaurant' | 'activity' | 'attraction',
  destination?: string
): string => {
  // Map 'airline' to 'flight' for the new API
  const mappedType = type === 'airline' ? 'flight' : type;
  const searchTerm = name + (location ? ' ' + location : '');
  return getBookingLink(providedUrl, searchTerm, mappedType, destination);
};

/**
 * @deprecated Use buildGuaranteedUrl() instead. Kept for backward compatibility.
 */
export const buildSmartFallbackUrl = (
  name: string,
  type: 'airline' | 'hotel' | 'restaurant' | 'activity' | 'attraction',
  destination?: string
): string => {
  const mappedType = type === 'airline' ? 'flight' : type;
  return buildGuaranteedUrl(name, mappedType, destination);
};

// ─── LOGISTICS VALIDATION ───────────────────────────────────────────

/**
 * Extracts the domain name from a URL for display purposes.
 */
const extractDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'unknown';
  }
};

/**
 * Validates and fixes all booking URLs in a LogisticsData object.
 * Uses getBookingLink() to guarantee every option has a working URL.
 */
export const validateAndFixLinks = (
  logistics: LogisticsData,
  destination?: string,
  _dates?: { startDate?: string; endDate?: string }
): LogisticsData => {
  let validCount = 0;
  let replacedCount = 0;
  let totalCount = 0;

  const validateOption = (
    option: LogisticsOption,
    type: 'flight' | 'hotel'
  ): LogisticsOption => {
    totalCount++;
    const originalUrl = option.bookingUrl || '';
    const urlIsValid = isValidBookingUrl(originalUrl) && !isLegacyAffiliateRedirect(originalUrl);
    const urlIsHallucinated = urlIsValid && isLikelyHallucinatedUrl(originalUrl);

    if (urlIsValid && !urlIsHallucinated) {
      // URL format is valid and not hallucinated — use getBookingLink for sanitization + affiliate wrapping
      const guaranteedUrl = getBookingLink(
        originalUrl,
        option.name || '',
        type,
        destination,
        option.affiliateType
      );
      validCount++;
      return {
        ...option,
        bookingUrl: guaranteedUrl,
        linkValidation: {
          originalUrl,
          validatedUrl: guaranteedUrl,
          isOriginal: true,
          confidence: 1.0,
          domain: extractDomain(originalUrl)
        }
      };
    } else {
      // URL is invalid, missing, or hallucinated — go DIRECTLY to buildGuaranteedUrl
      // Do NOT pass the broken URL through getBookingLink which might return it back
      replacedCount++;
      const reason = urlIsHallucinated ? 'likely hallucinated' : 'invalid/missing';
      const fallbackUrl = buildGuaranteedUrl(option.name || 'Travel', type, destination);

      // Apply affiliate wrapping to the fallback URL
      const locationHint = [option.name, destination].filter(Boolean).join(', ');
      const finalUrl = option.affiliateType
        ? (() => {
            const wrapped = wrapAffiliateLink(fallbackUrl, option.affiliateType!, locationHint);
            return wrapped !== fallbackUrl ? wrapped : fallbackUrl;
          })()
        : fallbackUrl;

      console.warn(
        `[LinkValidation] Replaced ${type} URL for "${option.name}" (${reason}):`,
        `original="${originalUrl.substring(0, 60)}"`,
        `-> fallback="${finalUrl.substring(0, 60)}"`
      );
      return {
        ...option,
        bookingUrl: finalUrl,
        linkValidation: {
          originalUrl,
          validatedUrl: finalUrl,
          isOriginal: false,
          confidence: 1.0,
          domain: extractDomain(finalUrl)
        }
      };
    }
  };

  const result: LogisticsData = {
    ...logistics,
    flight: validateOption(logistics.flight, 'flight'),
    hotel: validateOption(logistics.hotel, 'hotel'),
    flightAlternatives: logistics.flightAlternatives?.map(alt =>
      validateOption(alt, 'flight')
    ),
    hotelAlternatives: logistics.hotelAlternatives?.map(alt =>
      validateOption(alt, 'hotel')
    ),
    airbnbs: logistics.airbnbs
  };

  console.log(
    `[LinkValidation] Summary: ${validCount}/${totalCount} links valid,`,
    `${replacedCount} replaced with fallbacks.`,
    `Accuracy: ${totalCount > 0 ? ((validCount / totalCount) * 100).toFixed(0) : 100}%`
  );

  return result;
};

// ─── ACTIVITY LINK VALIDATION ───────────────────────────────────────

/**
 * Validates and fixes all booking URLs in daily itinerary time slots.
 * Ensures every activity has a working Reserve link.
 */
export const validateAndFixActivityLinks = (
  itinerary: DayItinerary[],
  destination?: string
): DayItinerary[] => {
  if (!itinerary || itinerary.length === 0) return itinerary;

  let fixedCount = 0;
  let totalCount = 0;

  const fixTimeSlot = (slot: TimeSlot): TimeSlot => {
    if (!slot) return slot;
    totalCount++;

    const currentUrl = slot.bookingUrl || '';
    const urlValid = isValidBookingUrl(currentUrl) && !isLegacyAffiliateRedirect(currentUrl);
    const urlHallucinated = urlValid && isLikelyHallucinatedUrl(currentUrl);

    if (urlValid && !urlHallucinated) {
      return slot; // URL is valid and not hallucinated
    }

    // URL is missing, invalid, or hallucinated — go DIRECTLY to buildGuaranteedUrl
    fixedCount++;
    const reason = urlHallucinated ? 'likely hallucinated' : 'invalid/missing';
    const fallbackUrl = buildGuaranteedUrl(slot.activity || 'Experience', 'activity', destination);

    // Apply affiliate wrapping to fallback
    const locationHint = [slot.activity, destination].filter(Boolean).join(', ');
    const finalUrl = slot.affiliateType
      ? (() => {
          const wrapped = wrapAffiliateLink(fallbackUrl, slot.affiliateType!, locationHint);
          return wrapped !== fallbackUrl ? wrapped : fallbackUrl;
        })()
      : fallbackUrl;

    console.warn(
      `[LinkValidation] Replaced activity URL for "${slot.activity}" (${reason}):`,
      `original="${currentUrl.substring(0, 60)}"`,
      `-> fallback="${finalUrl.substring(0, 60)}"`
    );

    return {
      ...slot,
      bookingUrl: finalUrl
    };
  };

  const result = itinerary.map(day => {
    if (day.isPlaceholder) return day;
    return {
      ...day,
      morning: fixTimeSlot(day.morning),
      afternoon: fixTimeSlot(day.afternoon),
      evening: fixTimeSlot(day.evening),
      contingency: day.contingency ? fixTimeSlot(day.contingency) : day.contingency
    };
  });

  if (fixedCount > 0) {
    console.log(
      `[LinkValidation] Activities: Fixed ${fixedCount}/${totalCount} activity booking URLs with fallbacks.`
    );
  }

  return result;
};
